<template>
  <aside
    class="sidebar-container zone-sidebar"
    :class="{ collapsed }"
  >
    <!-- 品牌区 -->
    <div class="logo-area">
      <BrandLogoMark
        v-if="branding.useLogoImage"
        :logo-url="branding.logoUrl"
        :logo-caption="branding.logoCaption"
        :logo-alt="branding.logoAlt"
        variant="sm"
      />
      <div v-else class="logo-icon">Dme</div>
      <span class="logo-text">
        <span class="main only">{{ branding.productName }}</span>
        <span class="sub">股东智能决策系统</span>
      </span>
    </div>
    <!-- 折叠按钮 -->
    <button type="button" class="collapse-btn" aria-label="折叠菜单" @click="collapsed = !collapsed">
      <span v-if="collapsed">→</span>
      <span v-else>←</span>
    </button>
    <!-- 菜单：一级可折叠，当前所属分组默认展开 -->
    <nav class="sidebar-menu-wrap nav-menu">
      <template v-for="group in visibleMenuGroups" :key="group.title">
        <div v-if="group.items.length" class="nav-group">
          <button
            type="button"
            class="nav-group-btn"
            :class="{ expanded: isGroupOpen(group) }"
            @click="toggleGroup(group.title)"
          >
            <span class="nav-group-btn-inner">
              <component :is="group.items[0].icon" class="nav-item-icon" />
              <span class="nav-item-text">{{ group.title }}</span>
            </span>
            <span class="nav-group-arrow">{{ isGroupOpen(group) ? '▼' : '▶' }}</span>
          </button>
          <div v-show="isGroupOpen(group)" class="nav-group-children">
            <router-link
              v-for="item in group.items"
              :key="item.path"
              :to="item.path"
              class="nav-item"
              :class="{ active: isActive(item.path) }"
              :title="item.tooltip"
            >
              <component :is="item.icon" class="nav-item-icon" />
              <span class="nav-item-text">{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </nav>
    <!-- 底部用户区 -->
    <div v-show="!collapsed" class="sidebar-user">
      <div class="sidebar-user-inner">
        <div class="sidebar-user-avatar">{{ userInitials }}</div>
        <div class="sidebar-user-role">{{ userRoleLabel }}</div>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { resolveBrandingFromWindow } from '@/config/tenantBranding.js'
import BrandLogoMark from '@/components/BrandLogoMark.vue'
import '@/styles/brand-logo-mark.css'

const branding = resolveBrandingFromWindow()
const emit = defineEmits(['collapse-change'])
import {
  DataBoard,
  PieChart,
  TrendCharts,
  List,
  UserFilled,
  UploadFilled,
  DocumentAdd,
  DataLine,
  Setting,
  User,
  ScaleToOriginal,
  StarFilled,
  Connection,
  WarningFilled,
  Message,
  Check,
  Lock,
  Key,
  Cpu,
  Document
} from '@element-plus/icons-vue'

const route = useRoute()
const collapsed = ref(false)
const currentUser = ref(null)
const allowedPermissions = ref([])
/** 当前展开的一级分组（默认仅当前路由所属分组） */
const expandedGroupKeys = ref(new Set())

// 菜单配置：分组 + 项（path、文案、权限码、图标、tooltip）
const menuGroups = [
  {
    title: '数据驾驶舱',
    items: [
      { path: '/report', label: '数据报告', permission: 'data-report', icon: Document, tooltip: '当期核心变化简报，供董事长快速概览' },
      { path: '/dashboard/penetration', label: '股权穿透', permission: 'penetration', icon: TrendCharts, tooltip: '查看核心股东控制关系及持股变化趋势' },
      { path: '/dashboard/chip', label: '筹码成本', permission: 'chip', icon: PieChart, tooltip: '查看筹码成本分布及盈亏情况' },
      { path: '/dashboard/trade', label: '交易趋势', permission: 'trade', icon: List, tooltip: '查看股东买卖行为趋势与交易流水' },
    ]
  },
  {
    title: '股东管理',
    items: [
      { path: '/core/index', label: '核心股东配置', permission: 'core', icon: User, tooltip: '维护核心股东名单，后续规则与控制列表均基于此' },
      { path: '/core/control-list', label: '核心股东控制', permission: 'core-control-list', icon: Connection, tooltip: '查看与维护「核心股东-普通股东」关联，可手动增删改' },
      { path: '/suspicious/index', label: '疑似关联处理', permission: 'suspicious-push', icon: Message, tooltip: '查看系统识别的疑似关联，推送给助理做线下确认' },
      { path: '/dashboard/shareholder-uniqueness', label: '股东档案', permission: 'shareholder-uniqueness', icon: UserFilled, tooltip: '按股东唯一性查看通讯信息、首次/最近出现、持仓期数' },
    ]
  },
  {
    title: '规则配置',
    items: [
      { path: '/rule', label: '基础规则', permission: 'rule', icon: ScaleToOriginal, tooltip: '配置地址/证件等匹配规则及置信度，用于关联识别' },
      { path: '/rule-special', label: '特殊规则', permission: 'rule-special', icon: StarFilled, tooltip: '配置代持等特殊关系规则' },
    ]
  },
  {
    title: '助理工作台',
    items: [
      { path: '/data/upload-shareholder', label: '上传名册', permission: 'upload-shareholder', icon: DocumentAdd, tooltip: '上传股东名册 Excel，解析后入库' },
      { path: '/data/stock-price', label: '股票价格', permission: 'stock-price', icon: DataLine, tooltip: '维护股票价格数据，用于成本与盈亏计算' },
      { path: '/suspicious/confirm', label: '确认疑似关联', permission: 'suspicious-confirm', icon: Check, tooltip: '助理确认或驳回疑似关联，结果回传领导' },
    ]
  },
  {
    title: '权限与反馈',
    items: [
      { path: '/permission', label: '用户权限', permission: 'permission', icon: Key, tooltip: '管理用户、角色及访问权限' },
      { path: '/feedback', label: '问题反馈', permission: 'feedback', icon: WarningFilled, tooltip: '提交问题反馈' },
    ]
  }
]

const hasMenuPermission = (code) => {
  const list = allowedPermissions.value
  if (!Array.isArray(list) || list.length === 0) return false
  return list.includes(code)
}

// 按权限过滤后的分组
const visibleMenuGroups = computed(() => {
  return menuGroups.map(group => {
    const items = group.items.filter(item => {
      if (!item.permission) return true
      return hasMenuPermission(item.permission)
    })
    return { ...group, items }
  }).filter(group => {
    if (group.title === '权限与反馈') return hasMenuPermission('permission')
    return true
  })
})

/** 当前路由所属的一级分组 title */
const defaultOpenGroupKey = computed(() => {
  const path = route.path
  for (const group of visibleMenuGroups.value) {
    if (group.items.some(item => item.path === path)) return group.title
  }
  return null
})

/** 该一级分组是否展开（默认仅当前所属分组展开） */
function isGroupOpen(group) {
  return expandedGroupKeys.value.has(group.title)
}

function toggleGroup(title) {
  const next = new Set(expandedGroupKeys.value)
  if (next.has(title)) {
    next.delete(title)
  } else {
    next.clear()
    next.add(title)
  }
  expandedGroupKeys.value = next
}

// 路由变化时：只展开当前路由所属的一级分组
watch(
  () => defaultOpenGroupKey.value,
  (key) => {
    if (key) expandedGroupKeys.value = new Set([key])
  },
  { immediate: true }
)

// 同步折叠状态给父组件（用于 main-wrap margin-left）
watch(collapsed, (val) => { emit('collapse-change', val) }, { immediate: true })

const isActive = (path) => route.path === path

const userInitials = computed(() => {
  const u = currentUser.value
  if (!u || !u.username) return 'ME'
  const s = (u.username || '').trim().toUpperCase()
  if (s.length >= 2) return s.slice(0, 2)
  return s || 'ME'
})

const userRoleLabel = computed(() => {
  const u = currentUser.value
  if (!u || !u.role) return '用户'
  if (u.role === 'admin') return 'FINTECH ADMIN'
  if (u.role === 'chairman') return '领导'
  if (u.role === 'secretary') return '助理'
  return u.role
})

onMounted(async () => {
  try {
    const raw = localStorage.getItem('currentUser')
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        currentUser.value = parsed
        allowedPermissions.value = parsed.permissions || []
      } catch (e) {
        // ignore
      }
    }
    const res = await axios.get('/api/auth/me/')
    if (res.data.status === 'ok') {
      const user = res.data.data || {}
      currentUser.value = user
      allowedPermissions.value = user.permissions || []
      localStorage.setItem('currentUser', JSON.stringify(user))
    }
  } catch (e) {
    // 未登录或接口异常时保持现状
  }
})
</script>
